import { cn } from '@ui/lib/utils';

import { StarRating } from '../starRating/StarRating';

export interface RatingsSummaryProps {
  average: number;
  total: number;
  /** Distribution counts for stars 1-5: [dist1, dist2, dist3, dist4, dist5] */
  distribution: [number, number, number, number, number];
  className?: string;
  title?: string;
  hideTitle?: boolean;
  /** 'horizontal' places score and bars side-by-side; 'vertical' stacks them. */
  layout?: 'horizontal' | 'vertical';
}

export const RatingsSummary = ({
  average,
  total,
  distribution,
  className,
  title = 'Ratings and Reviews',
  hideTitle = false,
  layout = 'horizontal'
}: RatingsSummaryProps) => {
  const isVertical = layout === 'vertical';

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {!hideTitle && <h3 className="text-base md:text-lg font-semibold text-neutral-800">{title}</h3>}
      <div className={cn(isVertical ? 'flex flex-col gap-4' : 'flex items-center gap-4 md:gap-6')}>
        <div className={cn('flex flex-col gap-1 shrink-0', 'items-start')}>
          <span className="text-2xl md:text-3xl font-medium text-neutral-800">{average.toFixed(1)}</span>
          <StarRating value={average} size="sm" readonly />
          <span className="text-xs text-neutral-800">({total.toLocaleString()})</span>
        </div>

        <div className="flex flex-col gap-1 md:gap-1.5 flex-1 min-w-0">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = distribution[star - 1];
            const pct = total > 0 ? (count / total) * 100 : 0;
            return (
              <div key={star} className="flex items-center gap-1.5 md:gap-2">
                <span className="text-[10px] md:text-xs text-neutral-800 text-center w-[6px] md:w-[8px] shrink-0">
                  {star}
                </span>
                <div className="flex-1 h-1 md:h-1.5 bg-neutral-100 rounded-lg overflow-hidden">
                  <div className="h-full bg-primary rounded-lg transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
